import { get } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import defaultInstitutionLogo from '@/assets/icons/bank.svg';
import { formMixin, validators } from '@/components/forms/mixin';
import alertBox from '@/components/generics/alert-box/alert-box.vue';
import bankLinks from '@/components/wallet/bank-links/bank-links.vue';
import depositAmountOverview from '@/components/wallet/shared/deposit-amount-overview/deposit-amount-overview.vue';
import depositAmountPicker from '@/components/wallet/shared/deposit-amount-picker/deposit-amount-picker.vue';
import depositDisclaimer from '@/components/wallet/shared/deposit-disclaimer/deposit-disclaimer.vue';
import depositDoneLinks from '@/components/wallet/shared/deposit-done-links/deposit-done-links.vue';
import depositInfoOverview from '@/components/wallet/shared/deposit-info-overview/deposit-info-overview.vue';
import depositResult from '@/components/wallet/shared/deposit-result/deposit-result.vue';
import { DEPOSIT_METHOD_TYPE } from '@/components/wallet/shared/depositMethodTypes';
import feeStatusMessage from '@/components/wallet/shared/fee-status-message/fee-status-message.vue';
import feeWaivedBanner from '@/components/wallet/shared/fee-waived-banner/fee-waived-banner.vue';
import promoCodeInput from '@/components/wallet/shared/promo-code-input/promo-code-input.vue';
import bankTransferIntent from '@/helpers/bankTransferIntent';
import { withMeta } from '@/helpers/dataLayer';
import getValidationMessage from '@/helpers/getValidationMessage';
import log from '@/logger';
import depositWarningMixin from '@/mixins/depositWarningMixin';
import documentUploadMixin from '@/mixins/documentUpload';
import loaderMixin from '@/mixins/loader';
import popupPageModeMixin from '@/mixins/popupPageModeMixin';
import promoCodeMixin from '@/mixins/promoCode';
import { isNumber, roundToTwo } from '@/utils/helpers';

const DEPOSIT_CHIP_AMOUNTS = [10, 25, 50];

const STEPS = {
  SELECT_ACCOUNT: 1,
  ENTER_AMOUNT: 2,
  REVIEW: 3,
  SUCCESS: 4,
  FAILED: 5,
};

const FAILURE_REASONS = {
  ACCOUNT_PERMISSION_DENIED: 3,
  BANK_ACCOUNT_NOT_FOUND: 9,
  SUBMISSION_STATUS_UNCONFIRMED: 11,
  PROCESSING_ERROR: 12,
  INTERNAL_ERROR: 16,
  BANK_CONNECTION_REAUTHENTICATION_REQUIRED: 17,
};

const FAILURE_MESSAGES = {
  1: 'Identity verification is required before you can deposit.',
  2: 'This PredictIt account is not active.',
  3: 'Bank transfer deposits are not available for this account.',
  4: 'Please upload the requested documents before depositing.',
  5: 'Your uploaded documents are still being reviewed.',
  6: 'Enter a valid deposit amount.',
  7: 'This deposit exceeds your current deposit limit.',
  8: 'Please provide the requested identity information before depositing.',
  9: 'The selected bank account is no longer available. Choose or reconnect an account.',
  10: 'This deposit request could not be verified. Please review it and try again.',
  11: 'We could not confirm the final status of a recent deposit. Do not submit another deposit. Contact Support.',
  12: 'We could not process this deposit. Please try again.',
  13: 'The deposit request was invalid. Please review it and try again.',
  14: 'Deposits are temporarily unavailable while trading is suspended.',
  15: 'Your PredictIt account could not be found.',
  16: 'We could not process this deposit. Please try again later.',
  17: 'Sign in again to reconnect this bank account before depositing.',
  18: 'The selected bank account does not have enough available funds for this deposit.',
  19: 'The selected bank account is not eligible for this deposit.',
  20: 'We could not verify this bank account right now. Please try again later.',
  21: "We couldn't verify your bank balance right now. Please try again later.",
};

function errorData(error) {
  return get(error, 'response.data', {}) || {};
}

function responseMessage(data, fallback) {
  return (
    data.errorMessage ||
    data.message ||
    data.Message ||
    FAILURE_MESSAGES[Number(data.failureReason)] ||
    fallback
  );
}

export default {
  mixins: [
    depositWarningMixin,
    documentUploadMixin,
    formMixin,
    loaderMixin,
    popupPageModeMixin,
    promoCodeMixin,
  ],
  components: {
    alertBox,
    bankLinks,
    depositAmountOverview,
    depositAmountPicker,
    depositDisclaimer,
    depositDoneLinks,
    depositInfoOverview,
    depositResult,
    feeStatusMessage,
    feeWaivedBanner,
    promoCodeInput,
  },
  data() {
    return {
      amount: '',
      activeOperation: '',
      operationStartedAt: 0,
      operationSeconds: 0,
      operationTimer: null,
      ownsLoader: false,
      isTransferViewDestroyed: false,
      requestGeneration: 0,
      canRetryPendingDeposit: false,
      canDeposit: false,
      confirmedPromoAmount: 0,
      confirmedPromoCampaignName: '',
      currentStep: STEPS.SELECT_ACCOUNT,
      defaultInstitutionLogo,
      depositAmounts: DEPOSIT_CHIP_AMOUNTS,
      depositMethodType: DEPOSIT_METHOD_TYPE.TRANSFERS,
      errorMessage: '',
      failureMessage: '',
      failureReason: null,
      feeFreeAmount: 0,
      formDisclaimer: '',
      processingFeeCap: null,
      processingFeeAmount: '0.00',
      processingFeePercent: 0,
      promoReviewErrorMessage: '',
      reusableSubmissionToken: '',
      selectedAccount: null,
      submissionFailureNeedsIdempotentRetry: false,
      submissionToken: '',
      totalToWallet: '0.00',
      wasFeeDiscountApplied: false,
      STEPS,
    };
  },
  validations() {
    return {
      amount: validators.depositAmountCoinbase(
        this.minimumDepositAmount,
        this.maximumDepositAmount,
      ),
    };
  },
  computed: {
    ...mapGetters('app', ['isDepositPromoInputBoxEnabled', 'isLayoutMobile']),
    ...mapState('wallet', ['minimumDepositAmount']),
    ...mapState('user', ['ssnInfoVerified', 'status', 'userData']),
    operationMessage() {
      if (this.activeOperation === 'review') {
        return this.operationSeconds >= 5
          ? 'Still preparing your deposit review. Please wait...'
          : 'Preparing your deposit review...';
      }
      if (this.activeOperation === 'submission') {
        return 'Checking your bank account and processing your deposit. Please do not submit another deposit.';
      }
      if (this.activeOperation === 'recovery') {
        return 'Checking the status of your existing deposit. No new deposit is being sent.';
      }
      return 'Preparing your bank account for this deposit...';
    },
    accountDescription() {
      if (!this.selectedAccount) return 'Bank account';
      const accountName =
        this.selectedAccount.accountName || this.selectedAccount.accountType || 'Bank account';
      return this.selectedAccount.accountNumberLastFour
        ? `${accountName} - ${this.selectedAccount.accountNumberLastFour}`
        : accountName;
    },
    accountSummary() {
      if (!this.selectedAccount) return 'Bank account';
      const institutionName = this.selectedAccount.institutionName;
      if (!institutionName || institutionName === this.accountDescription) {
        return this.accountDescription;
      }
      return this.accountDescription === 'Bank account'
        ? institutionName
        : `${institutionName} • ${this.accountDescription}`;
    },
    accountUploadDocumentTheme() {
      return this.isPage ? 'page-padded' : '';
    },
    amountHasError() {
      return this.$v.amount.$error;
    },
    amountValidationMessage() {
      return getValidationMessage(this.$v.amount);
    },
    classModifiers() {
      return {
        'add-funds-transfers': true,
        'add-funds-transfers--page': this.isPage,
        'add-funds-transfers--popup': this.isPopup,
      };
    },
    displayDisclaimer() {
      return this.formDisclaimer || this.disclaimer;
    },
    hasReviewPromo() {
      return this.confirmedPromoAmount > 0;
    },
    isAmbiguousSubmission() {
      return (
        this.submissionFailureNeedsIdempotentRetry ||
        this.failureReason === FAILURE_REASONS.SUBMISSION_STATUS_UNCONFIRMED
      );
    },
    hasUnresolvedIntent() {
      // Only a held submission token can be reconciled; an unconfirmed review has nothing to keep.
      return this.isAmbiguousSubmission && !!this.submissionToken;
    },
    isConfirmDisabled() {
      return this.invalidOrDisabled || !this.canDeposit;
    },
    maximumDepositAmount() {
      return 99999;
    },
    reviewPromoLabel() {
      return this.promoAppliedCode || this.confirmedPromoCampaignName || 'Applied';
    },
    successAmount() {
      return `$${Number(this.amount || 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
  },
  methods: {
    ...mapActions('wallet', [
      'confirmDepositBankTransfer',
      'getBalance',
      'getDepositBankTransferForm',
      'getDepositMethods',
      'getLinkedBankAccounts',
      'submitDepositBankTransfer',
      'restoreBankTransferIntent',
      'clearBankTransferIntent',
      'getDepositBankTransferStatus',
    ]),
    ...mapActions('popup', ['setCurrentPopup']),
    applyFeeSnapshot(data) {
      const feePercent = Number(data.feePercent || 0);
      const net =
        data.net === null || typeof data.net === 'undefined'
          ? Number(this.amount || 0)
          : Number(data.net);
      this.processingFeePercent = roundToTwo(feePercent * 100);
      this.processingFeeAmount = Number(Math.abs(data.fee || 0)).toFixed(2);
      this.totalToWallet = Number(net + this.confirmedPromoAmount).toFixed(2);
      this.wasFeeDiscountApplied = data.wasFeeDiscountApplied === true;
      this.feeFreeAmount = Number(data.feeFreeAmount || 0);
      this.processingFeeCap =
        data.feeCap === null || typeof data.feeCap === 'undefined' ? null : Number(data.feeCap);
    },
    editAmount() {
      if (this.hasUnresolvedIntent || this.activeOperation) return;
      this.failureMessage = '';
      this.failureReason = null;
      // This remains only a reuse candidate until the server returns it from a new review.
      this.reusableSubmissionToken = this.submissionToken;
      this.submissionToken = '';
      this.currentStep = STEPS.ENTER_AMOUNT;
      if (!this.canDeposit) this.loadDepositForm();
    },
    finishRequest() {
      if (this.ownsLoader) this.loaderRemoveLegacy();
      this.ownsLoader = false;
      if (this.operationTimer) clearInterval(this.operationTimer);
      this.operationTimer = null;
      this.activeOperation = '';
      this.formEnable();
    },
    startOperation(stage) {
      this.requestGeneration += 1;
      this.activeOperation = stage;
      this.operationStartedAt = Date.now();
      this.operationSeconds = 0;
      if (this.operationTimer) clearInterval(this.operationTimer);
      this.operationTimer = setInterval(() => {
        this.operationSeconds = Math.floor((Date.now() - this.operationStartedAt) / 1000);
      }, 1000);
      this.formDisable();
      // The shared mobile loader has a five-second watchdog; this operation owns
      // its persistent inline status instead of incrementing that shared counter.
      if (!this.isLayoutMobile) {
        this.loaderAddLegacy();
        this.ownsLoader = true;
      }
      this.$nextTick(() => {
        const progress = this.$refs.operationProgress;
        if (
          !this.isTransferViewDestroyed &&
          this.activeOperation &&
          this.isLayoutMobile &&
          progress
        )
          progress.scrollIntoView({ block: 'center' });
      });
      return this.requestGeneration;
    },
    isCurrentOperation(generation) {
      return !this.isTransferViewDestroyed && generation === this.requestGeneration;
    },
    restorePendingDeposit() {
      if (!this.userData.userName || this.activeOperation) return Promise.resolve();
      const owner = this.userData.userName;
      const generation = this.requestGeneration;
      return this.restoreBankTransferIntent().then((intent) => {
        if (!intent || !this.isCurrentOperation(generation) || this.userData.userName !== owner)
          return;
        this.selectedAccount = { linkedBankAccountId: intent.linkedBankAccountId };
        this.amount = String(intent.amount);
        this.submissionToken = intent.submissionToken;
        this.submissionFailureNeedsIdempotentRetry = true;
        this.failureMessage =
          'The final status of your previous deposit is not confirmed yet. Do not submit another deposit.';
        this.currentStep = STEPS.FAILED;
        this.restorePendingAccountLabel(intent, owner);
        this.checkPendingDeposit();
      });
    },
    restorePendingAccountLabel(intent, owner) {
      // Resolve display-only fields from owned accounts, never persist them with the intent.
      // This optional lookup must not delay checking the financial outcome.
      return this.getLinkedBankAccounts({ timeout: 4000 })
        .then((data) => {
          if (
            this.isTransferViewDestroyed ||
            this.userData.userName !== owner ||
            this.submissionToken !== intent.submissionToken ||
            !this.selectedAccount ||
            this.selectedAccount.linkedBankAccountId !== intent.linkedBankAccountId
          )
            return;
          const accounts = data && data.success === true && data.linkedBankAccounts;
          const account =
            Array.isArray(accounts) &&
            accounts.find((item) => item.linkedBankAccountId === intent.linkedBankAccountId);
          if (!account) return;
          this.selectedAccount = {
            linkedBankAccountId: intent.linkedBankAccountId,
            institutionName: account.institutionName,
            accountName: account.accountName,
            accountType: account.accountType,
            accountNumberLastFour: account.accountNumberLastFour,
          };
        })
        .catch(() => null);
    },
    checkPendingDeposit() {
      if (this.activeOperation || !this.submissionToken || !this.selectedAccount) return;
      const generation = this.startOperation('recovery');
      const owner = this.userData.userName;
      const submissionToken = this.submissionToken;
      this.canRetryPendingDeposit = false;
      this.getDepositBankTransferStatus({
        submissionToken,
        linkedBankAccountId: this.selectedAccount.linkedBankAccountId,
        amount: Number(this.amount),
      })
        .then((data) => {
          if (
            !this.isCurrentOperation(generation) ||
            this.userData.userName !== owner ||
            this.submissionToken !== submissionToken
          )
            return;
          this.finishRequest();
          if (data && (data.state === 'succeeded' || data.state === 'failed')) {
            this.clearBankTransferIntent({ owner, submissionToken });
            this.submissionFailureNeedsIdempotentRetry = false;
            if (data.state === 'succeeded') {
              this.applyFeeSnapshot(data);
              this.failureReason = null;
              this.failureMessage = '';
              this.currentStep = STEPS.SUCCESS;
              this.getBalance().catch(() => null);
            } else {
              this.failureReason = Number(data.failureReason) || FAILURE_REASONS.PROCESSING_ERROR;
              this.failureMessage = responseMessage(
                data,
                'This deposit was not completed. You can review it again.',
              );
              this.currentStep = STEPS.FAILED;
            }
            return;
          }
          this.submissionFailureNeedsIdempotentRetry = true;
          this.canRetryPendingDeposit = !!data && data.state === 'unknown';
          this.failureMessage =
            data && data.state === 'pending'
              ? 'Your deposit is still being processed. Check its status again shortly; do not submit another deposit.'
              : 'We cannot confirm the final deposit status yet. Check again later or contact Support. Do not submit another deposit.';
        })
        .catch(() => {
          if (
            !this.isCurrentOperation(generation) ||
            this.userData.userName !== owner ||
            this.submissionToken !== submissionToken
          )
            return;
          this.finishRequest();
          this.submissionFailureNeedsIdempotentRetry = true;
          this.failureMessage =
            'We could not check this deposit right now. Check again later or contact Support. Do not submit another deposit.';
        });
    },
    retryPendingDeposit() {
      if (!this.canRetryPendingDeposit || !this.isAmbiguousSubmission || this.activeOperation)
        return;
      this.canRetryPendingDeposit = false;
      // Deliberate same-intent replay only; the backend resolves it under its intent lock.
      this.onSubmitDeposit(true);
    },
    handleAmountBlur() {
      this.$v.amount.$touch();
      this.$gtm.trackEvent(
        withMeta({
          event: 'eventTrackerTrigger',
          category: 'Deposit',
          action: 'fill',
          label: 'Amount',
        }),
      );
    },
    handleFormFailure(error) {
      if (this.isTransferViewDestroyed) return;
      const data = errorData(error);
      this.finishRequest();
      if (this.checkDocumentUploadFlags(data)) return;
      this.returnToAccounts(
        responseMessage(
          data,
          'We could not prepare this bank account for a deposit. Please try again.',
        ),
      );
    },
    handleReviewFailure(error) {
      if (this.isTransferViewDestroyed) return;
      const data = errorData(error);
      this.finishRequest();
      if (this.checkDocumentUploadFlags(data)) return;
      this.failureReason = Number(data.failureReason) || null;
      this.failureMessage = responseMessage(
        data,
        'We could not review this deposit. Please try again.',
      );
      if (
        this.failureReason === FAILURE_REASONS.BANK_ACCOUNT_NOT_FOUND ||
        this.failureReason === FAILURE_REASONS.BANK_CONNECTION_REAUTHENTICATION_REQUIRED
      ) {
        this.returnToAccounts(this.failureMessage);
        return;
      }
      this.currentStep = STEPS.FAILED;
    },
    handleSubmitFailure(error) {
      if (this.isTransferViewDestroyed) return;
      const data = errorData(error);
      const failureReason = Number(data.failureReason) || null;
      this.submissionFailureNeedsIdempotentRetry = !bankTransferIntent.isDefinitiveFailureResponse(
        get(error, 'response'),
      );
      if (error.bankTransferNotSent) {
        this.submissionFailureNeedsIdempotentRetry = false;
      }
      this.finishRequest();
      if (error.existingBankTransferIntent) {
        this.restorePendingDeposit();
        return;
      }
      if (this.checkDocumentUploadFlags(data)) return;
      this.failureReason = failureReason;
      let fallbackMessage = 'We could not complete this deposit. Please try again.';
      if (this.submissionFailureNeedsIdempotentRetry) {
        fallbackMessage =
          'The deposit status is not confirmed. Check its status before attempting another deposit.';
      } else if (error.bankTransferNotSent) {
        fallbackMessage =
          'The deposit could not be sent. Please sign in again and allow browser session storage.';
      }
      this.failureMessage = responseMessage(data, fallbackMessage);
      if (
        this.failureReason === FAILURE_REASONS.BANK_ACCOUNT_NOT_FOUND ||
        this.failureReason === FAILURE_REASONS.BANK_CONNECTION_REAUTHENTICATION_REQUIRED
      ) {
        this.returnToAccounts(this.failureMessage);
        return;
      }
      this.currentStep = STEPS.FAILED;
    },
    isStep(name) {
      return this.currentStep === STEPS[name];
    },
    onAccounts(data = {}) {
      this.promoCodeDetail = data.promoCodeDetail || {};
    },
    loadDepositForm() {
      if (!this.selectedAccount) return Promise.resolve();
      const generation = this.startOperation('form');
      return this.getDepositBankTransferForm({
        linkedBankAccountId: this.selectedAccount.linkedBankAccountId,
        depositPromoCode: this.promoApplied ? this.promoAppliedCode : '',
      })
        .then((data) => {
          if (!this.isCurrentOperation(generation)) return;
          this.finishRequest();
          if (this.checkDocumentUploadFlags(data)) return;
          this.canDeposit = data.canDeposit === true;
          this.errorMessage = data.errorMessage || '';
          this.formDisclaimer = data.disclaimer || '';
          this.promoCodeDetail = data.promoCodeDetail || {};
          if (!this.canDeposit) {
            this.returnToAccounts(
              this.errorMessage || 'Bank transfer deposits are not available right now.',
            );
            return;
          }
          this.processingFeePercent = roundToTwo(Number(data.feePercent || 0) * 100);
          this.processingFeeCap =
            data.feeCap === null || typeof data.feeCap === 'undefined' ? null : Number(data.feeCap);
          this.errorMessage = '';
          this.currentStep = STEPS.ENTER_AMOUNT;
          this.$nextTick(() => this.$v.amount.$reset());
        })
        .catch((error) => {
          if (this.isCurrentOperation(generation)) this.handleFormFailure(error);
        });
    },
    onReviewAndConfirm() {
      this.$v.amount.$touch();
      if (!this.formIsValid() || !this.canDeposit || this.isFormDisabled) {
        return;
      }
      const generation = this.startOperation('review');
      this.$gtm.trackEvent(
        withMeta({
          event: 'eventTrackerTrigger',
          category: 'Deposit',
          action: 'submit',
          label: 'Submit Deposit',
        }),
      );
      this.confirmDepositBankTransfer({
        linkedBankAccountId: this.selectedAccount.linkedBankAccountId,
        amount: Number(this.amount),
        depositPromoCode: this.promoApplied ? this.promoAppliedCode : '',
        submissionToken: this.reusableSubmissionToken || null,
      })
        .then((data) => {
          if (!this.isCurrentOperation(generation)) return;
          this.finishRequest();
          if (this.checkDocumentUploadFlags(data)) return;
          if (!data.success || !data.canDeposit || !data.submissionToken) {
            this.failureReason = Number(data.failureReason) || null;
            this.failureMessage = responseMessage(
              data,
              'We could not review this deposit. Please try again.',
            );
            this.currentStep = STEPS.FAILED;
            return;
          }
          this.submissionToken = data.submissionToken;
          this.reusableSubmissionToken = data.submissionToken;
          this.confirmedPromoAmount = Number(data.promoAmount || 0);
          this.confirmedPromoCampaignName = data.promoCampaignName || '';
          this.promoReviewErrorMessage = data.promoErrorMessage || '';
          this.applyFeeSnapshot(data);
          this.currentStep = STEPS.REVIEW;
          this.scrollToTopIfPage();
        })
        .catch((error) => {
          if (this.isCurrentOperation(generation)) this.handleReviewFailure(error);
        });
    },
    onSubmitDeposit(isSameIntentRetry = false) {
      if (
        !this.selectedAccount ||
        !this.submissionToken ||
        this.isFormDisabled ||
        (this.isAmbiguousSubmission && isSameIntentRetry !== true)
      )
        return;
      const generation = this.startOperation('submission');
      this.$gtm.trackEvent(
        withMeta({
          event: 'eventTrackerTrigger',
          category: 'Deposit Confirm',
          action: 'submit',
          label: 'Confirm Deposit',
        }),
      );
      this.submitDepositBankTransfer({
        linkedBankAccountId: this.selectedAccount.linkedBankAccountId,
        amount: Number(this.amount),
        submissionToken: this.submissionToken,
      })
        .then((data) => {
          if (!this.isCurrentOperation(generation)) return;
          this.finishRequest();
          this.submissionFailureNeedsIdempotentRetry =
            !data || (data.success !== true && !bankTransferIntent.isDefinitiveFailure(data));
          if (!data.success) {
            this.failureReason = Number(data.failureReason) || null;
            this.failureMessage = responseMessage(
              data,
              'We could not complete this deposit. Please try again.',
            );
            this.currentStep = STEPS.FAILED;
            return;
          }
          this.applyFeeSnapshot(data);
          this.failureMessage = '';
          this.failureReason = null;
          this.submissionFailureNeedsIdempotentRetry = false;
          this.currentStep = STEPS.SUCCESS;
          this.$gtm.trackEvent(
            withMeta({
              event: this.status && this.status.hasBeenFunded ? 'MultiDeposit' : 'Deposit',
            }),
          );
          this.$emit('success');
          this.getBalance().catch((error) =>
            log.error('Failed to refresh balance after deposit.', error),
          );
          this.getDepositMethods().catch((error) =>
            log.error('Failed to refresh deposit methods after deposit.', error),
          );
          this.scrollToTopIfPage();
        })
        .catch((error) => {
          if (this.isCurrentOperation(generation)) this.handleSubmitFailure(error);
        });
    },
    onSuccessDone() {
      this.resetFlow();
      if (this.isPopup) {
        this.setCurrentPopup(null);
      }
    },
    onUpdateAmount(value) {
      if (this.amount === '') {
        this.amount = value.toFixed(2);
      } else if (isNumber(this.amount) || !isNaN(Number(this.amount))) {
        this.amount = (Number(this.amount) + value).toFixed(2);
      }
      this.$gtm.trackEvent(
        withMeta({
          event: 'eventTrackerTrigger',
          category: 'Deposit',
          action: 'click',
          label: `Amount ${value} Button`,
        }),
      );
    },
    resetFlow() {
      if (this.hasUnresolvedIntent || this.activeOperation) return;
      this.amount = '';
      this.canDeposit = false;
      this.confirmedPromoAmount = 0;
      this.confirmedPromoCampaignName = '';
      this.currentStep = STEPS.SELECT_ACCOUNT;
      this.errorMessage = '';
      this.failureMessage = '';
      this.failureReason = null;
      this.feeFreeAmount = 0;
      this.formDisclaimer = '';
      this.processingFeeCap = null;
      this.processingFeeAmount = '0.00';
      this.promoCodeDetail = {};
      this.promoReviewErrorMessage = '';
      this.reusableSubmissionToken = '';
      this.selectedAccount = null;
      this.submissionFailureNeedsIdempotentRetry = false;
      this.canRetryPendingDeposit = false;
      this.submissionToken = '';
      this.totalToWallet = '0.00';
      this.wasFeeDiscountApplied = false;
      this.onResetPromoCode();
      this.formEnable();
      this.$nextTick(() => this.$v.amount.$reset());
    },
    retryFailedRequest() {
      if (this.activeOperation) return;
      if (this.hasUnresolvedIntent) {
        this.checkPendingDeposit();
        return;
      }
      const failureReason = this.failureReason;
      this.failureMessage = '';
      this.failureReason = null;
      this.submissionFailureNeedsIdempotentRetry = false;

      // A confirmed terminal provider result has consumed this intent. Review
      // it again so the server can rotate the token while reusing an eligible
      // same-bank, same-or-lower 15-minute assessment.
      if (failureReason === FAILURE_REASONS.PROCESSING_ERROR && this.submissionToken) {
        this.reusableSubmissionToken = this.submissionToken;
        this.submissionToken = '';
        if (!this.canDeposit) {
          this.loadDepositForm();
          return;
        }
        this.onReviewAndConfirm();
        return;
      }

      if (this.submissionToken) {
        this.editAmount();
        return;
      }

      this.currentStep = STEPS.ENTER_AMOUNT;
    },
    returnToAccounts(message) {
      if (this.hasUnresolvedIntent || this.activeOperation) return;
      this.amount = '';
      this.canDeposit = false;
      this.currentStep = STEPS.SELECT_ACCOUNT;
      this.errorMessage = message || '';
      this.failureMessage = '';
      this.failureReason = null;
      this.formDisclaimer = '';
      this.reusableSubmissionToken = '';
      this.selectedAccount = null;
      this.submissionFailureNeedsIdempotentRetry = false;
      this.submissionToken = '';
      this.formEnable();
      this.$nextTick(() => this.$v.amount.$reset());
    },
    scrollToTopIfPage() {
      if (this.isLayoutMobile && this.isPage) {
        setTimeout(() => window.scrollTo(0, 0), 100);
      }
    },
    selectAccount(account) {
      if (this.hasUnresolvedIntent || this.activeOperation) return;
      if (!account || !account.linkedBankAccountId) return;
      this.amount = '';
      this.errorMessage = '';
      this.failureMessage = '';
      this.failureReason = null;
      this.reusableSubmissionToken = '';
      this.selectedAccount = account;
      this.submissionFailureNeedsIdempotentRetry = false;
      this.submissionToken = '';
      this.loadDepositForm();
    },
  },
  mounted() {
    this.getDepositMethods();
    this.restorePendingDeposit();
  },
  beforeUnmount() {
    this.requestGeneration += 1;
    this.finishRequest();
    this.isTransferViewDestroyed = true;
  },
  watch: {
    'userData.userName': function restoreForAuthenticatedAccount() {
      this.requestGeneration += 1;
      this.finishRequest();
      this.submissionFailureNeedsIdempotentRetry = false;
      this.failureReason = null;
      this.resetFlow();
      this.restorePendingDeposit();
    },
    ssnInfoVerified(value) {
      if (value && this.selectedAccount) {
        this.collectSsnInfo = false;
        this.loadDepositForm();
      }
    },
  },
};
